import { Tooltip, type TooltipPropsInterface } from '@zillow/constellation-10';

type IconButtonTooltipProps = {
  content: string;
  enableHoverDelay?: boolean;
  placement?: TooltipPropsInterface['placement'];
  trigger: React.ReactNode;
};

/**
 * Tooltip for an icon button that performs an action (delete, star, restore, archive, etc.).
 * The caller supplies the action icon as `trigger`.
 *
 * This is not a help affordance. For a "what is this?" help tooltip, don't use this wrapper:
 * render a default-trigger Constellation `Tooltip`/`Popover` (which shows the question-mark
 * `TriggerButton`) inside a centered row — `css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}`.
 * See the HelpTooltip pattern: `modules/visual-cohesion/src/patterns/HelpTooltip.stories.tsx`.
 */
export default function IconButtonTooltip({
  content,
  enableHoverDelay = true,
  trigger,
  placement = 'top',
}: IconButtonTooltipProps) {
  const hoverProps = enableHoverDelay ? { delay: { open: 300, close: 0 } } : undefined;

  return (
    <Tooltip
      content={content}
      placement={placement}
      shouldAwaitInteractionResponse
      shouldCloseOnViewportLeave
      trigger={trigger}
      useHoverProps={hoverProps}
    />
  );
}
